import React, { useState, useEffect } from "react";
import {
  X,
  Filter,
  RotateCcw,
  ChevronDown,
  Sparkles,
  Zap,
  Clock,
  AlertTriangle,
  Ban,
} from "lucide-react";

// Custom Select Component
const CustomSelect = ({ label, value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-neutral-dark mb-2">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-2.5 bg-white border rounded-xl text-left transition-all duration-200 ${
          isOpen
            ? "border-primary ring-2 ring-primary/20"
            : "border-neutral-light hover:border-neutral"
        }`}
      >
        <span className={selectedOption ? "text-neutral-dark" : "text-neutral"}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-neutral transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-2 bg-white border border-neutral-light rounded-xl shadow-lg py-1 max-h-60 overflow-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                  value === option.value
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-neutral-dark hover:bg-neutral-lightest"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Filter Tag Component
const FilterTag = ({ label, value, onRemove }) => (
  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium group">
    <span className="text-neutral text-xs">{label}:</span>
    <span>{value}</span>
    <button
      onClick={onRemove}
      className="p-0.5 hover:bg-primary/20 rounded transition-colors"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </span>
);

// Quick Filter Button Component
const QuickFilterButton = ({
  icon: Icon,
  label,
  onClick,
  variant = "default",
}) => {
  const variants = {
    default:
      "bg-white border-neutral-light text-neutral-dark hover:bg-primary hover:text-white hover:border-primary",
    warning:
      "bg-white border-neutral-light text-neutral-dark hover:bg-warning hover:text-white hover:border-warning",
    danger:
      "bg-white border-neutral-light text-neutral-dark hover:bg-danger hover:text-white hover:border-danger",
    success:
      "bg-white border-neutral-light text-neutral-dark hover:bg-success hover:text-white hover:border-success",
  };

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-medium transition-all duration-200 ${variants[variant]}`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
};

const PricingRuleFilters = ({ filters, onFilterChange, onClearFilters }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync local filters with props
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClear = () => {
    const clearedFilters = {
      active: null,
      productType: null,
      branchCode: null,
      priority: null,
      purityLevel: null,
      makingChargeType: null,
      dateRange: null,
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
  };

  const hasActiveFilters = Object.values(localFilters).some(
    (value) => value !== null && value !== "" && value !== undefined
  );

  const activeFilterCount = Object.values(localFilters).filter(
    (value) => value !== null && value !== "" && value !== undefined
  ).length;

  // Option configurations
  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];

  const productTypeOptions = [
    { value: "", label: "All Types" },
    { value: "GOLD", label: "Gold" },
    { value: "SILVER", label: "Silver" },
    { value: "DIAMOND", label: "Diamond" },
    { value: "PLATINUM", label: "Platinum" },
    { value: "GEMSTONE", label: "Gemstone" },
  ];

  const purityOptions = [
    { value: "", label: "All Purities" },
    { value: "24K", label: "24K" },
    { value: "22K", label: "22K" },
    { value: "18K", label: "18K" },
    { value: "14K", label: "14K" },
    { value: "10K", label: "10K" },
    { value: "916", label: "916" },
    { value: "999", label: "999" },
  ];

  const priorityOptions = [
    { value: "", label: "All Priorities" },
    { value: "critical", label: "Critical (80-100)" },
    { value: "high", label: "High (50-79)" },
    { value: "medium", label: "Medium (20-49)" },
    { value: "low", label: "Low (0-19)" },
  ];

  const chargeTypeOptions = [
    { value: "", label: "All Types" },
    { value: "PERCENTAGE", label: "Percentage" },
    { value: "PER_GRAM", label: "Per Gram" },
    { value: "FIXED", label: "Fixed Amount" },
    { value: "SLAB", label: "Slab Based" },
    { value: "TIERED", label: "Tiered" },
  ];

  const dateRangeOptions = [
    { value: "", label: "All Dates" },
    { value: "active", label: "Currently Active" },
    { value: "future", label: "Future Start" },
    { value: "expiring", label: "Expiring Soon (7 days)" },
    { value: "expired", label: "Expired" },
  ];

  // Get display value for filter tag
  const getFilterDisplayValue = (field, value) => {
    const optionMaps = {
      active: statusOptions,
      productType: productTypeOptions,
      purityLevel: purityOptions,
      priority: priorityOptions,
      makingChargeType: chargeTypeOptions,
      dateRange: dateRangeOptions,
    };

    if (field === "active") {
      return value === true || value === "true" ? "Active" : "Inactive";
    }

    const options = optionMaps[field];
    if (options) {
      const option = options.find((opt) => opt.value === value);
      return option?.label || value;
    }
    return value;
  };

  return (
    <div className="space-y-5">
      {/* Filter Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-neutral-dark">Filter Rules</h3>
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 bg-primary text-white text-xs font-bold rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={handleClear}
            className="inline-flex items-center gap-1.5 text-sm text-danger hover:text-danger-dark font-medium transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset All</span>
          </button>
        )}
      </div>

      {/* Primary Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CustomSelect
          label="Status"
          value={
            localFilters.active === null
              ? ""
              : localFilters.active?.toString() || ""
          }
          onChange={(val) =>
            handleChange("active", val === "" ? null : val === "true")
          }
          options={statusOptions}
          placeholder="All Status"
        />

        <CustomSelect
          label="Product Type"
          value={localFilters.productType || ""}
          onChange={(val) =>
            handleChange("productType", val === "" ? null : val)
          }
          options={productTypeOptions}
          placeholder="All Types"
        />

        <CustomSelect
          label="Purity Level"
          value={localFilters.purityLevel || ""}
          onChange={(val) =>
            handleChange("purityLevel", val === "" ? null : val)
          }
          options={purityOptions}
          placeholder="All Purities"
        />

        <CustomSelect
          label="Priority"
          value={localFilters.priority || ""}
          onChange={(val) => handleChange("priority", val === "" ? null : val)}
          options={priorityOptions}
          placeholder="All Priorities"
        />
      </div>

      {/* Secondary Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Branch Code - Text Input */}
        <div>
          <label className="block text-sm font-medium text-neutral-dark mb-2">
            Branch Code
          </label>
          <input
            type="text"
            placeholder="e.g., BR001"
            value={localFilters.branchCode || ""}
            onChange={(e) =>
              handleChange(
                "branchCode",
                e.target.value === "" ? null : e.target.value
              )
            }
            className="w-full px-4 py-2.5 bg-white border border-neutral-light rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 placeholder:text-neutral"
          />
        </div>

        <CustomSelect
          label="Making Charge Type"
          value={localFilters.makingChargeType || ""}
          onChange={(val) =>
            handleChange("makingChargeType", val === "" ? null : val)
          }
          options={chargeTypeOptions}
          placeholder="All Types"
        />

        <CustomSelect
          label="Validity Period"
          value={localFilters.dateRange || ""}
          onChange={(val) => handleChange("dateRange", val === "" ? null : val)}
          options={dateRangeOptions}
          placeholder="All Dates"
        />
      </div>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <span className="text-sm font-medium text-neutral mr-2">Active:</span>

          {localFilters.active !== null && (
            <FilterTag
              label="Status"
              value={getFilterDisplayValue("active", localFilters.active)}
              onRemove={() => handleChange("active", null)}
            />
          )}

          {localFilters.productType && (
            <FilterTag
              label="Type"
              value={localFilters.productType}
              onRemove={() => handleChange("productType", null)}
            />
          )}

          {localFilters.purityLevel && (
            <FilterTag
              label="Purity"
              value={localFilters.purityLevel}
              onRemove={() => handleChange("purityLevel", null)}
            />
          )}

          {localFilters.priority && (
            <FilterTag
              label="Priority"
              value={getFilterDisplayValue("priority", localFilters.priority)}
              onRemove={() => handleChange("priority", null)}
            />
          )}

          {localFilters.branchCode && (
            <FilterTag
              label="Branch"
              value={localFilters.branchCode}
              onRemove={() => handleChange("branchCode", null)}
            />
          )}

          {localFilters.makingChargeType && (
            <FilterTag
              label="Charge"
              value={localFilters.makingChargeType}
              onRemove={() => handleChange("makingChargeType", null)}
            />
          )}

          {localFilters.dateRange && (
            <FilterTag
              label="Date"
              value={getFilterDisplayValue("dateRange", localFilters.dateRange)}
              onRemove={() => handleChange("dateRange", null)}
            />
          )}
        </div>
      )}

      {/* Quick Filter Presets */}
      <div className="pt-4 border-t border-neutral-light/50">
        <p className="text-sm font-medium text-neutral-dark mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          Quick Filters
        </p>
        <div className="flex flex-wrap gap-2">
          <QuickFilterButton
            icon={Zap}
            label="High Priority Active"
            variant="default"
            onClick={() => {
              handleChange("active", true);
              handleChange("priority", "high");
            }}
          />
          <QuickFilterButton
            icon={Sparkles}
            label="Active Gold Rules"
            variant="default"
            onClick={() => {
              handleChange("active", true);
              handleChange("productType", "GOLD");
            }}
          />
          <QuickFilterButton
            icon={Clock}
            label="Expiring Soon"
            variant="warning"
            onClick={() => {
              handleChange("dateRange", "expiring");
            }}
          />
          <QuickFilterButton
            icon={Ban}
            label="Inactive Rules"
            variant="danger"
            onClick={() => {
              handleChange("active", false);
            }}
          />
          <QuickFilterButton
            icon={AlertTriangle}
            label="Slab Based"
            variant="default"
            onClick={() => {
              handleChange("makingChargeType", "SLAB");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PricingRuleFilters;
